/** @module */
import Object3D from "_src/engine/object";
import arrayTools from "_src/engine/utility/arrays";
import Batcher from "_src/engine/batcher";
import { vec3, mat4 } from "_src/libs/gl-matrix";
import GL from "_src/engine/gl";
import output from "_src/engine/output";

const _vec3Tmp = vec3.create();

export default class Scene extends Object3D {

  constructor() {

    super();

    this.batcher = new Batcher();

    this.camera = null;

    this.framebuffer = null;
    this.resolvedFramebuffer = null;
    this.clearColor = [0.0, 0.0, 0.0, 0.0];

    this.modelMatrix = mat4.create();

    this._enabled = true;

    this.lightPositions = new Float32Array();
    this.lightColors = new Float32Array();
    this._lightsNumber = 0;
    this.cameraTrans = vec3.create();

    this.texelSize = new Float32Array([0.0, 0.0]);

    this.needsReadPixels = false;
    this.readPixelsViewport = null;
    this.readPixelsResolve = null;

    this.clearedColor = true;
    this.clearedDepth = true;
    this.clearedStencil = true;

    this.isDepthPrepass = false;
    this.isPostBlending = false;
    this.isObjectIDPass = false;
  }

  setEnabled(enabled) {
    this._enabled = enabled;
  }

  getEnabled() {
    return this._enabled;
  }

  resize(width, height) {

    if (this.camera) {
      this.camera.setViewport(width, height);
    }

    if (this.framebuffer) {
      this.framebuffer.resize(width, height);
      this.framebuffer.reportMemoryUsage(this);
    }

    if (this.resolvedFramebuffer) {
      this.resolvedFramebuffer.resize(width, height);
      this.resolvedFramebuffer.reportMemoryUsage(this);
    }

    this.texelSize[0] = 1.0 / width;
    this.texelSize[1] = 1.0 / height;

  }

  getFramebuffer() {
    return this.framebuffer;
  }

  setFramebuffer(framebuffer) {
    if (this.framebuffer) {
      this.framebuffer.reportMemoryRelease(this);
    }
    this.framebuffer = framebuffer;
    if (this.framebuffer) {
      this.framebuffer.reportMemoryUsage(this);
    }
  }

  getResolvedBuffer() {
    return this.resolvedFramebuffer;
  }

  setResolvedBuffer(resolvedFramebuffer) {
    if (this.resolvedFramebuffer) {
      this.resolvedFramebuffer.reportMemoryRelease(this);
    }
    this.resolvedFramebuffer = resolvedFramebuffer;
    if (this.resolvedFramebuffer) {
      this.resolvedFramebuffer.reportMemoryUsage(this);
    }
  }

  appendCamera(camera) {

    this.camera = camera;

    const viewport = camera.viewport;

    this.texelSize[0] = 1.0 / viewport[2];
    this.texelSize[1] = 1.0 / viewport[3];

  }

  add(object) {

    if (this._children.includes(object)) {
      return;
    }

    object.setParent(this);

    this._children.push(object);

    //low performance. Think about "by request" resource counting model
    //this._reportObjectMemoryState(object, true);

    this.batcher.addObject(object);

  }

  remove(obj) {

    const ind = this._children.indexOf(obj);
    if (ind === -1) {
      return;
    }

    this._removeByInd(ind);

    //this._reportObjectMemoryState(obj, false);

  }

  _removeByInd(ind) {
    if (ind >= 0 && ind < this._children.length) {
      const obj = this._children.splice(ind, 1)[0];
      this.batcher.removeObject(obj);
    }
  }

  removeChildren() {
    for (const child of this._children) {
      this._reportObjectMemoryState(child, false);
    }
    this._children.length = 0;
    this.batcher.cleanup();
  }

  setClearColor(r, g, b, a) {
    this.clearColor[0] = r;
    this.clearColor[1] = g;
    this.clearColor[2] = b;
    this.clearColor[3] = a;
  }

  updateTransforms() {

    if (this.camera) {
      this.camera.updateMatrix();
    }

    const children = this._children;

    for (let i = 0; i < children.length; ++i) {
      children[i].updateMatrix();
    }

  }

  getBufferPixels(viewport) {

    this.needsReadPixels = true;

    return new Promise((resolve, reject) => {
      this.readPixelsViewport = viewport ? viewport :
          this.camera ? this.camera.viewport : null;
      if (!this.readPixelsViewport) {
        reject("Invalid viewport.");
      }
      this.readPixelsResolve = resolve;
    }).catch((error) => {
      output.error(output.DEVELOPERS_ERROR, "Can't read pixels from gpu. " + error);
    });

  }

  readPixels() {

    const viewport = this.readPixelsViewport;
    const leftBottomX = viewport[0];
    const leftBottomY = viewport[1];
    const width = viewport[2];
    const height = viewport[3];
    const size = 4 * width * height;

    const arrayBuffer = arrayTools.getArrayBuffer(size);
    const pixels = new Uint8Array(arrayBuffer);

    if (GL.checkWebGL2()) {
      const promiseResolve = this.readPixelsResolve;
      GL.readPixelsAsync(this.framebuffer, leftBottomX, leftBottomY, width, height, pixels).then(() => {
        promiseResolve(pixels);
      });
    } else {
      GL.readPixelsSync(this.framebuffer, leftBottomX, leftBottomY, width, height, pixels);
      this.readPixelsResolve(pixels);
    }

    this.needsReadPixels = false;
    this.readPixelsViewport = null;
    this.readPixelsResolve = null;

  }

  setLightPosition(lightID, position) {
    const offset = 3 * lightID;
    this.lightPositions[offset]   = position[0];
    this.lightPositions[offset+1] = position[1];
    this.lightPositions[offset+2] = position[2];
  }

  setLightColor(lightID, r, g, b) {
    const offset = 3 * lightID;
    this.lightColors[offset]   = r;
    this.lightColors[offset+1] = g;
    this.lightColors[offset+2] = b;
  }

  getLightsCount() {
    return this._lightsNumber;
  }

  updateLights(lights) {

    const camera = this.camera;
    const numLights = lights.length;

    if (camera === null) {
      return;
    }

    const camNear = vec3.set(_vec3Tmp, 0, 0, Math.min(0.0, camera.near));
    vec3.transformQuat(camNear, camNear, camera.quat);
    const lightTrans = vec3.sub(_vec3Tmp, camera.trans, camNear);

    vec3.copy(this.cameraTrans, lightTrans);

    this._setLightsCount(numLights);

    for (let i = 0; i < lights.length; ++i) {
      const light = lights[i];
      const color = light.color;

      this.setLightColor(i, color.r, color.g, color.b);
      this.setLightPosition(i, lightTrans);
    }

  }

  _reportObjectMemoryState(object, isAllocation) {
    if (object.isDrawable) {
      if (isAllocation) {
        object.reportMemoryUsage(this);
      } else {
        object.reportMemoryRelease();
      }
    }

    for (const child of object.getAllNestedChildren()) {
      if (child.isDrawable) {
        if (isAllocation) {
          child.reportMemoryUsage(this);
        } else {
          child.reportMemoryRelease();
        }
      }
    }
  }

  _setLightsCount(numLights) {

    if (this._lightsNumber === numLights) {
      return;
    }

    const oldLightDirections = this.lightPositions;
    const oldLightColors     = this.lightColors;

    if (this._lightsNumber < numLights) {
      this.lightPositions = new Float32Array(3 * numLights);
      this.lightColors     = new Float32Array(3 * numLights);
      this.lightPositions.set(oldLightDirections);
      this.lightColors.set(oldLightColors);
    } else {
      this.lightPositions = oldLightDirections.slice(0, 3 * numLights);
      this.lightColors = oldLightDirections.slice(0, 3 * numLights);
    }

    this._lightsNumber = numLights;

  }

}
